import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	FormControl,
	FormControlLabel,
	MenuItem,
	Radio,
	RadioGroup,
	TextField,
	Typography,
} from "@mui/material";
import { MesfModal } from "@dexteel/mesf-core";
import {
	useAssignRollToRollset,
	useRemoveRollFromRollset,
	useRollChangeReasons,
	useRollsForAssign,
} from "../hooks/useRollshopOperations";
import type { RollsetListItem } from "../models/RollshopOperations";

export type RollPosition = "TOP" | "BOTTOM" | "1" | "2" | "3" | "4" | "5";

interface Props {
	open: boolean;
	rollset: RollsetListItem | null;
	position: RollPosition | null;
	currentRollId: number | null;
	currentRollName: string | null;
	onClose: () => void;
	onSuccess: () => void;
}

// Combo modal — like the legacy `displayChangeRollModal`:
// - If the position currently HOLDS a roll, show "Remove" form
//   (action = FREE or SCRAPPED, plus reason + comments).
// - If the position is EMPTY, show "Assign" form (filter rolls FREE or
//   SCRAPPED with the rollset's mill size).
export const ChangeRollPositionModal = ({
	open,
	rollset,
	position,
	currentRollId,
	currentRollName,
	onClose,
	onSuccess,
}: Props) => {
	const isAssigning = currentRollId == null;

	const [includeScrapped, setIncludeScrapped] = useState(false);
	const [rollId, setRollId] = useState<number | null>(null);
	const [actionType, setActionType] = useState<"FREE" | "SCRAPPED">("FREE");
	const [reasonId, setReasonId] = useState<number | null>(null);
	const [comments, setComments] = useState("");
	const [errorMsg, setErrorMsg] = useState("");

	useEffect(() => {
		if (open) {
			setIncludeScrapped(false);
			setRollId(null);
			setActionType("FREE");
			setReasonId(null);
			setComments("");
			setErrorMsg("");
		}
	}, [open]);

	const rollsQuery = useRollsForAssign(
		rollset?.MillSizeId ?? null,
		isAssigning && open,
	);
	const reasonsQuery = useRollChangeReasons(!isAssigning && open);

	const filteredRolls = useMemo(() => {
		const all = rollsQuery.data ?? [];
		if (includeScrapped) return all;
		return all.filter((r) => r.StatusCode === "FREE");
	}, [rollsQuery.data, includeScrapped]);

	// When user switches to SCRAPPED, force them to pick a reason mapped to
	// SCRAPPED status (matching legacy `RollStatusCode` → `RollStatusCode` link).
	const filteredReasons = useMemo(() => {
		const all = reasonsQuery.data ?? [];
		if (actionType === "FREE") return all; // legacy: change reason still selectable
		return all.filter((r) => r.RollStatusCode === "SCRAPPED");
	}, [reasonsQuery.data, actionType]);

	const { assignRoll, isAssigning: isAssigningPending } =
		useAssignRollToRollset({
			onSuccess: () => {
				onSuccess();
			},
			onError: (e) => setErrorMsg(e.message),
		});

	const { removeRoll, isRemoving } = useRemoveRollFromRollset({
		onSuccess: () => {
			onSuccess();
		},
		onError: (e) => setErrorMsg(e.message),
	});

	const isPending = isAssigningPending || isRemoving;

	const canSave = isAssigning
		? rollId != null && rollset?.Id != null && position != null
		: rollset?.Id != null &&
			position != null &&
			(actionType === "FREE" || reasonId != null);

	const handleSave = () => {
		if (!rollset?.Id || !position) return;
		setErrorMsg("");
		if (isAssigning && rollId != null) {
			assignRoll({
				rollsetId: rollset.Id,
				rollId,
				rollPosition: position,
			});
		} else if (!isAssigning) {
			removeRoll({
				rollsetId: rollset.Id,
				rollPosition: position,
				newStatusCode: actionType,
				changeReasonId: actionType === "FREE" ? null : reasonId,
				comments: comments.trim() || null,
			});
		}
	};

	const title = isAssigning
		? `ASSIGN ROLL TO POSITION ${position ?? ""}`
		: `REMOVE ROLL ${currentRollName ?? ""} FROM POSITION ${position ?? ""}`;

	return (
		<MesfModal
			title={title}
			open={open}
			handleClose={onClose}
			id="change-roll-position-modal"
			maxWidth="sm"
		>
			<MesfModal.Content style={{ padding: "20px 30px" }}>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					<Typography variant="body2" color="text.secondary">
						Rollset: <strong>{rollset?.Name ?? ""}</strong>
					</Typography>

					{isAssigning ? (
						<>
							<FormControlLabel
								control={
									<Radio
										size="small"
										checked={!includeScrapped}
										onChange={() => setIncludeScrapped(false)}
									/>
								}
								label="Free rolls only"
							/>
							<FormControlLabel
								control={
									<Radio
										size="small"
										checked={includeScrapped}
										onChange={() => setIncludeScrapped(true)}
									/>
								}
								label="Include scrapped rolls"
								sx={{ mt: -1.5 }}
							/>
							<TextField
								select
								size="small"
								label="Roll *"
								value={rollId == null ? "" : String(rollId)}
								onChange={(e) =>
									setRollId(
										e.target.value === "" ? null : Number(e.target.value),
									)
								}
								disabled={rollsQuery.isFetching}
								helperText={
									rollsQuery.isFetching
										? "Loading rolls..."
										: filteredRolls.length === 0
											? "No rolls available with current filter"
											: undefined
								}
							>
								<MenuItem value="">—</MenuItem>
								{filteredRolls.map((r) => (
									<MenuItem key={r.Id} value={String(r.Id)}>
										{r.Name}
										{r.StatusCode === "SCRAPPED" ? "  (scrapped)" : ""}
									</MenuItem>
								))}
							</TextField>
						</>
					) : (
						<>
							<FormControl>
								<RadioGroup
									row
									value={actionType}
									onChange={(_, v) => {
										setActionType(v as "FREE" | "SCRAPPED");
										setReasonId(null);
									}}
								>
									<FormControlLabel
										value="FREE"
										control={<Radio size="small" />}
										label="Set roll FREE"
									/>
									<FormControlLabel
										value="SCRAPPED"
										control={<Radio size="small" />}
										label="Scrap roll"
									/>
								</RadioGroup>
							</FormControl>

							{actionType === "SCRAPPED" && (
								<TextField
									select
									size="small"
									label="Scrap reason *"
									value={reasonId == null ? "" : String(reasonId)}
									onChange={(e) =>
										setReasonId(
											e.target.value === "" ? null : Number(e.target.value),
										)
									}
									disabled={reasonsQuery.isFetching}
								>
									<MenuItem value="">—</MenuItem>
									{filteredReasons.map((r) => (
										<MenuItem key={r.Id} value={String(r.Id)}>
											{r.Name}
										</MenuItem>
									))}
								</TextField>
							)}

							<TextField
								size="small"
								label="Comments"
								value={comments}
								onChange={(e) => setComments(e.target.value)}
								inputProps={{ maxLength: 400 }}
								multiline
								rows={2}
							/>
						</>
					)}

					{errorMsg && (
						<Box sx={{ color: "error.main", fontSize: 13 }}>{errorMsg}</Box>
					)}
				</Box>
			</MesfModal.Content>
			<MesfModal.Actions style={{ padding: "15px 30px 20px" }}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 1,
						width: "100%",
					}}
				>
					<Button
						variant="text"
						color="inherit"
						onClick={onClose}
						disabled={isPending}
					>
						CANCEL
					</Button>
					<Button
						variant="contained"
						color={isAssigning ? "primary" : "warning"}
						onClick={handleSave}
						disabled={!canSave || isPending}
						startIcon={
							isPending && <CircularProgress size={16} color="inherit" />
						}
					>
						{isPending ? "SAVING..." : isAssigning ? "ASSIGN" : "REMOVE"}
					</Button>
				</Box>
			</MesfModal.Actions>
		</MesfModal>
	);
};
